import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject, Subject } from 'rxjs';

import { CommunicationService } from './communication.service';
import { BasicError, Error403 } from '../models/error/error';
import { Activity } from '../models/activity';
import { Answersheet } from '../models/answersheet';

@Injectable()
export class UserStatisticsService {

  /**
   * Vars for get myActivities
   */
  private myActivitiesURL = '/api/v1/my/activities?sort=-id&limit=7';

  /**
   * Vars for get myAnswersheets
   */
  private myAnswersheetsURL = '/api/v1/my/answersheets?limit=1';

  /**
  * Vars for get myAnswersheetsFromToday
  */
  // private myAnswersheetsFromTodayURL = '/api/v1/my/answersheets?limit=1&collected_at>2017-11-22&collected_at<2017-11-24';
  private myAnswersheetsFromTodayURL = '/api/v1/my/answersheets?limit=1&';

  /**
   * Vars for get collected_at
   */
  private myAnswersheetsCollectedAtURL = '/api/v1/my/answersheets?sort=-id&limit=1';

  // Var for filterd answersheet function
  private myAnswersheetsFilterURL = '/api/v1/my/answersheets';
  private answersheetDetailURL = '/api/v1/answersheets/';

  constructor(private communication: CommunicationService) { }

  /**
   * This function returns my last 10 activities
   */
  public getMyActivities(): Subject<any> {
    const retReplay = new Subject();

    // Getting data from api
    const request = this.communication.get(this.myActivitiesURL);
    request.subscribe(json => {
      // Check if an error returned
      if (json instanceof BasicError) {
        retReplay.next(json);
        console.log('---UserStatisticsService | getMyActivities---: Error occured');
        return;
      }

      const retArr: Activity[] = [];

      for (let i = 0; i < json.data.length; i++) {
        const stdJSON = json.data[i];
        const activity = new Activity(stdJSON.id);
        if (stdJSON.attributes.text) { activity.setText(stdJSON.attributes.text) };
        if (stdJSON.attributes.name) { activity.setName(stdJSON.attributes.name) };
        retArr.push(activity);
      }
      retReplay.next(retArr);
    })

    return retReplay;
  }


  /**
   * This function returns the number of my total answersheets
   */
  public getMyTotalAnswersheets(): Subject<any> {
    const retReplay = new Subject();

    // Getting data form api
    const request = this.communication.get(this.myAnswersheetsURL);
    request.subscribe(json => {
      if (json instanceof BasicError) {
        retReplay.next(0);
        console.log('---UserStatisticsService | getMyTotalAnswersheets---: Error occured');
        return;
      }
      const total: number = json.meta.pagination.total;
      retReplay.next(total);
    })

    return retReplay;
  }

  /**
   * This function returns the number of answersheets from today
   */
  public getMyAnswersheetsFromToday(): Subject<any> {
    const retReplay = new Subject();

    const formatDate = this.dateFormatter();
    const splitted = formatDate.split(',');
    const yesterday = splitted[0];
    const tomorrow = splitted[1];

    const request = this.communication.get(this.myAnswersheetsFromTodayURL + 'collected_at>' + yesterday + '&collected_at<' + tomorrow);
    request.subscribe(json => {
      if (json instanceof BasicError) {
        retReplay.next(0);
        console.log('---UserStatisticsService | getMyAnswersheetsFromToday---: Error occured');
        return;
      }
      const total: number = json.meta.pagination.total;
      retReplay.next(total);
    })
    return retReplay;
  }
  /**
   * This function returns a datestring with yesterday-date & tommorow-date
   */
  public dateFormatter(): String {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    let toString = date.getFullYear() + '-';
    toString = ((date.getMonth() + 1) > 9) ? toString + (date.getMonth() + 1) : toString + '0' + (date.getMonth() + 1);
    toString = toString + '-';
    toString = (date.getDate() > 9) ? toString + date.getDate() : toString + '0' + date.getDate();
    date.setDate(date.getDate() - 2);
    let fromString = date.getFullYear() + '-';
    fromString = ((date.getMonth() + 1) > 9) ? fromString + (date.getMonth() + 1) : fromString + '0' + (date.getMonth() + 1);
    fromString = fromString + '-';
    fromString = (date.getDate() > 9) ? fromString + date.getDate() : fromString + '0' + date.getDate();

    const dateStr = fromString + ',' + toString;

    return dateStr;
  }

  public getCollectedAtFromToday(): Subject<any> {
    const retReplay = new Subject();
    const request = this.communication.get(this.myAnswersheetsCollectedAtURL);
    request.subscribe(json => {
      if (json instanceof BasicError) {
        retReplay.next(0);
        console.log('---UserStatisticsService | getCollectedAtFromToday---: Error occured');
        return;
      }


      let collAt = null;
      for (const answer of json.data) {
        collAt = answer.attributes.collected_at;
        break;
      }
      retReplay.next(collAt);
    })
    return retReplay;
  }

  /**
   * This function returns all answersheets
   * @param detail getting detailed
   * @param questionnaireId [optional] filtered by questionnaireId
   * @param from [optional] filtered from this date
   * @param to [optional] filtered to this date
   * @param limit [optional] limit (amount of answersheets)
   */
  public getAnswersheetsFiltered(detail: Boolean, questionnaireId?: String, from?: String, to?: String,
    limit?: Number, sort?: String): Subject<any> {
    const retReplay = new Subject();

    let url = this.myAnswersheetsFilterURL;

    // At limit if given, if not add limit=0
    if (limit === undefined) {
      url += '?limit=0';
    } else {
      url += '?limit=' + limit;
    }

    // Add sort
    if (sort === undefined) {
      url += '&sort=-id';
    } else {
      url += '&sort=' + sort;
    }

    // Add filter for questionnaireId if exists
    if (questionnaireId !== undefined && questionnaireId !== null) {
      url += '&questionnaire_id=' + questionnaireId;
    }

    // Add filter for from if exists
    if (from !== undefined && from !== null) {
      url += '&collected_at>' + from;
    }

    // Add filter `to` if exists
    if (to !== undefined && to !== null) {
      url += '&collected_at<' + to;
    }

    // Send request to CommunicationService
    this.communication.get(url).subscribe(json => {
      if (json instanceof BasicError) {
        retReplay.next(json);
        console.log('----UserStatisticsService: Failed to get User data');
        return;
      }

      // Getting each answersheet
      const answersheets = [];
      for (const ans of json.data) {
        const answersheet = new Answersheet(ans.id);
        if (ans.attributes.collected_at) { answersheet.setCollectedAt(ans.attributes.collected_at); }
        if (ans.attributes.questionnaire_id) { answersheet.setQuestionnaireId(ans.attributes.questionnaire_id); }

        answersheets.push(answersheet);
      }

      // Look if answersheets should be detailed or not
      if (detail) {
        if (answersheets.length === 0) {
          retReplay.next(answersheets);
        } else {
          this.getAnswersheetsDetailed(answersheets).subscribe(dd => {
            retReplay.next(dd);
          });
        }
      } else {
        retReplay.next(answersheets);
      }
    });

    return retReplay;
  }

  /**
   * Getting detailed answersheets
   * @param answersheets answersheets to get
   * @param count counting var for each answersheet
   */
  public getAnswersheetsDetailed(answersheets: Array<Answersheet>, count = 0, replay?: Subject<any>) {
    if (replay === undefined) {
      replay = new Subject();
    }

    this.getAnswersheetDetailed(answersheets[count.valueOf()]).subscribe(dd => {
      if (dd instanceof Answersheet) {
        answersheets[count] = dd;
      }

      count = count + 1;

      if (count < answersheets.length) {
        // Call the next answersheet
        this.getAnswersheetsDetailed(answersheets, count, replay);
      } else {
        replay.next(answersheets);
      }
    });

    return replay;
  }

  /**
   * This function returns the detailed answersheet
   * @param answersheet answersheet to get detailes from
   */
  public getAnswersheetDetailed(answersheet: Answersheet): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.answersheetDetailURL.toString() + answersheet.getId()).subscribe(json => {
      if (answersheet instanceof BasicError) {
        console.log('---------HCPQuestionnaireService-----: Could not get details of answersheet with id: ' + answersheet.getId());
        retReplay.next(answersheet);
        return;
      }

      if (json.data.attributes.collected_at !== undefined) { answersheet.setCollectedAt(json.data.attributes.collected_at); }

      if (json.data.attributes.locale !== undefined) {
        answersheet.setLocale(json.data.attributes.locale);
      }

      if (json.data.attributes.sensordata !== undefined) {
        answersheet.setSensorData(json.data.attributes.sensordata);
      }

      if (json.data.attributes.client !== undefined) {
        answersheet.setClient(json.data.attributes.client);
      }

      if (json.data.attributes.answers !== undefined) {
        answersheet.setAnswers(json.data.attributes.answers);
      } else {
        answersheet.setAnswers([]);
      }

      retReplay.next(answersheet);
    });

    return retReplay;
  }
}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/user-statistics.service.ts